import { FormEvent, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { createApproval } from '../../api/approval';
import { fetchOrgMembers } from '../../api/org';
import { MENU_APPROVAL } from '../layout/Header';
import {
  APPROVAL_TARGET_CLEAN_ACTIVITY_EVALUATION,
  APPROVAL_TARGET_CLEAN_ACTIVITY_SHEET,
  APPROVAL_TARGET_COMPLAINT_AS,
  APPROVAL_TARGET_EDUCATION_ANNUAL_PLAN,
  APPROVAL_TARGET_EDUCATION_RECORD,
  APPROVAL_TARGET_EXTERNAL_EDUCATION,
  APPROVAL_TARGET_INTERNAL_AUDIT_CHECKLIST,
  APPROVAL_TARGET_INTERNAL_AUDIT_PLAN,
  APPROVAL_TARGET_INTERNAL_AUDIT_REPORT,
  APPROVAL_TARGET_EXTERNAL_AUDIT,
  APPROVAL_TARGET_NCR,
  APPROVAL_TARGET_PROCESS_CHART,
  APPROVAL_TARGET_PRODUCT_MANUAL,
  APPROVAL_TARGET_DRAWING,
  APPROVAL_TARGET_INTERNAL_DRAFT,
  APPROVAL_TARGET_MEETING_MINUTES,
  APPROVAL_TARGET_OFFICIAL_DOCUMENT,
  APPROVAL_TARGET_IMPROVEMENT_SUGGESTION,
  APPROVAL_TARGET_SAFETY_PPE,
  APPROVAL_TARGET_SAFETY_CHECK,
  APPROVAL_TARGET_PRODUCTION_DAILY,
  APPROVAL_TARGET_QUALITY_POLICY_DETAIL,
  APPROVAL_TARGET_QUALITY_POLICY_PLAN,
  APPROVAL_TARGET_QUALITY_POLICY_REPORT,
  APPROVAL_TARGET_SMALL_GROUP,
  APPROVAL_TARGET_SMALL_GROUP_EVAL,
  APPROVAL_TARGET_SMALL_GROUP_PLAN,
  APPROVAL_TARGET_SMALL_GROUP_RECORD,
} from '../../types/approval';
import { canConfirmInspection, companyRoleLabel } from '../../types/companyRole';
import { OrgMemberSummary } from '../../types/org';
import './ApprovalRequestModal.css';

import { reportError } from '../../utils/notify';
import { notifyApprovalInboxChanged } from '../../utils/approvalInboxBadge';

const MANAGER_FINAL_TARGETS = new Set([
  APPROVAL_TARGET_CLEAN_ACTIVITY_SHEET,
  APPROVAL_TARGET_CLEAN_ACTIVITY_EVALUATION,
  APPROVAL_TARGET_COMPLAINT_AS,
  APPROVAL_TARGET_NCR,
  APPROVAL_TARGET_PRODUCTION_DAILY,
  APPROVAL_TARGET_INTERNAL_AUDIT_PLAN,
  APPROVAL_TARGET_INTERNAL_AUDIT_CHECKLIST,
  APPROVAL_TARGET_INTERNAL_AUDIT_REPORT,
  APPROVAL_TARGET_EXTERNAL_AUDIT,
  APPROVAL_TARGET_SMALL_GROUP,
  APPROVAL_TARGET_SMALL_GROUP_PLAN,
  APPROVAL_TARGET_SMALL_GROUP_RECORD,
  APPROVAL_TARGET_SMALL_GROUP_EVAL,
  APPROVAL_TARGET_QUALITY_POLICY_PLAN,
  APPROVAL_TARGET_QUALITY_POLICY_DETAIL,
  APPROVAL_TARGET_QUALITY_POLICY_REPORT,
  APPROVAL_TARGET_EDUCATION_ANNUAL_PLAN,
  APPROVAL_TARGET_EDUCATION_RECORD,
  APPROVAL_TARGET_EXTERNAL_EDUCATION,
  APPROVAL_TARGET_PROCESS_CHART,
  APPROVAL_TARGET_PRODUCT_MANUAL,
  APPROVAL_TARGET_DRAWING,
  APPROVAL_TARGET_INTERNAL_DRAFT,
  APPROVAL_TARGET_MEETING_MINUTES,
  APPROVAL_TARGET_OFFICIAL_DOCUMENT,
  APPROVAL_TARGET_IMPROVEMENT_SUGGESTION,
  APPROVAL_TARGET_SAFETY_PPE,
  APPROVAL_TARGET_SAFETY_CHECK,
]);

interface Props {
  open: boolean;
  titleDefault: string;
  targetType: string;
  targetId: number;
  onClose: () => void;
  onSubmitted?: () => void;
}

function toggleId(list: number[], id: number) {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

function memberPrimaryName(m: OrgMemberSummary) {
  return m.displayName || m.nickname || `사용자 ${m.userId}`;
}

function memberJobLabel(m: OrgMemberSummary) {
  const job = m.jobTitle?.trim();
  if (job) return job;
  return companyRoleLabel(m.role);
}

function memberDeptLabel(m: OrgMemberSummary) {
  return m.departmentName?.trim() || '미배치';
}

function ApprovalRequestModal({
  open,
  titleDefault,
  targetType,
  targetId,
  onClose,
  onSubmitted,
}: Props) {
  const navigate = useNavigate();
  const [members, setMembers] = useState<OrgMemberSummary[]>([]);
  const [title, setTitle] = useState(titleDefault);
  const [memo, setMemo] = useState('');
  const [viaUserIds, setViaUserIds] = useState<number[]>([]);
  const [consentUserIds, setConsentUserIds] = useState<number[]>([]);
  const [finalUserIds, setFinalUserIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setTitle(titleDefault);
    setMemo('');
    setViaUserIds([]);
    setConsentUserIds([]);
    setFinalUserIds([]);
    setError('');
    void (async () => {
      try {
        setLoading(true);
        const list = await fetchOrgMembers({ excludeSelf: true });
        setMembers(list);
      } catch {
        reportError(setError, '구성원을 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, titleDefault]);

  const memberMap = useMemo(() => {
    const map = new Map<number, OrgMemberSummary>();
    members.forEach((m) => map.set(m.userId, m));
    return map;
  }, [members]);

  const requiresManagerFinal = MANAGER_FINAL_TARGETS.has(targetType);

  const finalMembers = useMemo(
    () =>
      requiresManagerFinal
        ? members.filter((m) => canConfirmInspection(m.role))
        : members,
    [members, requiresManagerFinal]
  );

  if (!open) return null;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim()) {
      reportError(setError, '제목을 입력해 주세요.');
      return;
    }
    if (finalUserIds.length < 1) {
      reportError(setError, '최종 결재자를 1명 이상 지정해 주세요.');
      return;
    }
    if (
      requiresManagerFinal &&
      finalUserIds.some((id) => {
        const m = memberMap.get(id);
        return !canConfirmInspection(m?.role);
      })
    ) {
      reportError(setError, '최종 승인은 팀장 이상만 지정할 수 있습니다.');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      await createApproval({
        title: title.trim(),
        memo: memo.trim() || null,
        targetType,
        targetId,
        viaUserIds,
        consentUserIds,
        finalUserIds,
      });
      onSubmitted?.();
      onClose();
      notifyApprovalInboxChanged();
      navigate('/app', {
        replace: false,
        state: { activeMenu: MENU_APPROVAL, approvalTab: 'sent' },
      });
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '결재 요청에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderMemberChecks = (
    options: OrgMemberSummary[],
    selected: number[],
    onToggle: (id: number) => void,
    single?: boolean,
    emptyHint?: string
  ) => (
    <div className="approval-modal__members">
      {options.length === 0 && !loading ? (
        <p className="approval-modal__hint">
          {emptyHint || '선택 가능한 구성원이 없습니다.'}
        </p>
      ) : null}
      {options.map((m) => {
        const checked = selected.includes(m.userId);
        const name = memberPrimaryName(m);
        return (
          <label
            key={m.userId}
            className={`approval-modal__member${checked ? ' is-selected' : ''}`}
          >
            <input
              type={single ? 'radio' : 'checkbox'}
              name={single ? 'final-approver' : undefined}
              checked={checked}
              onChange={() => onToggle(m.userId)}
            />
            <span className="approval-modal__avatar" aria-hidden>
              {name.slice(0, 1)}
            </span>
            <span className="approval-modal__member-text">
              <strong>{name}</strong>
              <span className="approval-modal__member-meta">
                <span>{memberJobLabel(m)}</span>
                <span className="approval-modal__dot" aria-hidden />
                <span>{memberDeptLabel(m)}</span>
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );

  const renderSelectedChips = (ids: number[]) => {
    if (ids.length === 0) {
      return <span className="approval-modal__chip is-empty">미지정</span>;
    }
    return ids.map((id) => {
      const m = memberMap.get(id);
      const name = m ? memberPrimaryName(m) : String(id);
      return (
        <span key={id} className="approval-modal__chip">
          {name}
        </span>
      );
    });
  };

  return createPortal(
    <div className="approval-modal__backdrop" role="presentation" onClick={onClose}>
      <div
        className="approval-modal"
        role="dialog"
        aria-modal="true"
        aria-label="결재 요청"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="approval-modal__head">
          <div>
            <p className="approval-modal__eyebrow">APPROVAL</p>
            <h3>결재 요청</h3>
            <p className="approval-modal__sub">결재선을 구성한 뒤 요청을 올립니다.</p>
          </div>
          <button type="button" className="approval-modal__close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </header>

        <form className="approval-modal__form" onSubmit={handleSubmit}>
          <div className="approval-modal__scroll">
            {loading ? <p className="approval-modal__hint">구성원 불러오는 중...</p> : null}
            {error ? <p className="approval-modal__error">{error}</p> : null}

            <section className="approval-modal__section">
              <div className="approval-modal__section-head">
                <h4>요청 정보</h4>
              </div>
              <div className="approval-modal__fields">
                <label className="approval-modal__field">
                  <span>제목</span>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    maxLength={200}
                    placeholder="결재 제목"
                  />
                </label>
                <label className="approval-modal__field">
                  <span>메모</span>
                  <textarea
                    rows={3}
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                    placeholder="검토 시 참고할 내용 (선택)"
                  />
                </label>
              </div>
            </section>

            <section className="approval-modal__section">
              <div className="approval-modal__section-head">
                <h4>결재선</h4>
                <p>경유 → 첨부/합의 → 최종 순으로 진행됩니다. 본인은 결재선에 넣을 수 없습니다.</p>
              </div>

              <div className="approval-modal__route">
                <article className="approval-modal__step">
                  <div className="approval-modal__step-head">
                    <span className="approval-modal__step-no">1</span>
                    <div>
                      <h5>경유</h5>
                      <p>순서대로 확인</p>
                    </div>
                    <div className="approval-modal__chips">{renderSelectedChips(viaUserIds)}</div>
                  </div>
                  {renderMemberChecks(members, viaUserIds, (id) =>
                    setViaUserIds((prev) => toggleId(prev, id))
                  )}
                </article>

                <article className="approval-modal__step">
                  <div className="approval-modal__step-head">
                    <span className="approval-modal__step-no">2</span>
                    <div>
                      <h5>첨부 / 합의</h5>
                      <p>병렬 동의</p>
                    </div>
                    <div className="approval-modal__chips">{renderSelectedChips(consentUserIds)}</div>
                  </div>
                  {renderMemberChecks(members, consentUserIds, (id) =>
                    setConsentUserIds((prev) => toggleId(prev, id))
                  )}
                </article>

                <article className="approval-modal__step is-required">
                  <div className="approval-modal__step-head">
                    <span className="approval-modal__step-no">3</span>
                    <div>
                      <h5>
                        최종 결재 <em>필수</em>
                      </h5>
                      <p>
                        {requiresManagerFinal
                          ? '팀장 이상만 지정 가능'
                          : '마지막 승인'}
                      </p>
                    </div>
                    <div className="approval-modal__chips">{renderSelectedChips(finalUserIds)}</div>
                  </div>
                  {requiresManagerFinal ? (
                    <p className="approval-modal__hint">
                      청정활동 문서는 팀장·오너만 최종 승인할 수 있습니다.
                    </p>
                  ) : null}
                  {renderMemberChecks(
                    finalMembers,
                    finalUserIds,
                    (id) => setFinalUserIds([id]),
                    true,
                    requiresManagerFinal
                      ? '팀장 이상 구성원이 없습니다. 조직관리에서 직급을 확인해 주세요.'
                      : undefined
                  )}
                </article>
              </div>
            </section>
          </div>

          <footer className="approval-modal__footer">
            <button type="button" className="is-ghost" onClick={onClose} disabled={submitting}>
              취소
            </button>
            <button type="submit" disabled={submitting || loading}>
              {submitting ? '요청 중...' : '결재 요청'}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default ApprovalRequestModal;
